import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { X, ArrowRight, LogIn, UserPlus } from "lucide-react";
import GoogleButton from "@/components/auth/GoogleButton";
import VerifyEmailNotice from "@/components/auth/VerifyEmailNotice";

// Inline auth modal used for the TRCA CTA gate (Scenario A).
// Allows both login and signup, and on success calls onSuccess(user).
export default function AuthModal({ onClose, onSuccess, defaultMode = "login", message }) {
  const { login, register, loginWithGoogle } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState(defaultMode);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [sentTo, setSentTo] = useState("");

  const handle = (result) => {
    setLoading(false);
    if (result.ok) return onSuccess?.(result.user);
    if (result.verificationSent) { setSentTo(result.email); setMode("login"); return; }
    if (result.mfaResolver) { nav(`/login?next=${encodeURIComponent(window.location.pathname)}`); return; }
    setErr(result.error);
  };

  const google = async () => {
    setErr(""); setLoading(true);
    handle(await loginWithGoogle());
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    if (mode === "login") {
      handle(await login(form.email, form.password));
    } else {
      handle(await register({ name: form.name, email: form.email, password: form.password }));
    }
  };

  const inputCls = "w-full px-3 py-2.5 border border-ink-line bg-white rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand";

  return (
    <div className="fixed inset-0 bg-ink/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="auth-modal">
      <div className="bg-white max-w-md w-full border border-ink-line rounded-sm" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-ink-line">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-brand text-white grid place-items-center font-display font-bold text-sm rounded-sm">T</div>
            <div className="font-display font-bold tracking-tight">{mode === "login" ? "Sign in to continue" : "Create your account"}</div>
          </div>
          <button onClick={onClose} className="p-1 text-ink-muted hover:text-ink" data-testid="auth-modal-close"><X className="w-4 h-4" /></button>
        </div>

        <div className="px-6 pt-5">
          <p className="text-sm text-ink-muted">
            {message || "The TRCA Assessment is available to registered users. Sign in or create a free account to continue."}
          </p>

          <div className="mt-4 flex border border-ink-line rounded-sm overflow-hidden">
            <button
              onClick={() => { setMode("login"); setErr(""); }}
              data-testid="auth-tab-login"
              className={`flex-1 px-3 py-2 text-[13px] font-medium uppercase tracking-wider ${mode === "login" ? "bg-brand text-white" : "text-ink-muted hover:bg-secondary"}`}
            >
              Sign in
            </button>
            <button
              onClick={() => { setMode("register"); setErr(""); }}
              data-testid="auth-tab-register"
              className={`flex-1 px-3 py-2 text-[13px] font-medium uppercase tracking-wider ${mode === "register" ? "bg-brand text-white" : "text-ink-muted hover:bg-secondary"}`}
            >
              Create account
            </button>
          </div>
        </div>

        <form onSubmit={submit} className="px-6 py-5 space-y-4" data-testid="auth-modal-form">
          {sentTo && <VerifyEmailNotice email={sentTo} />}
          <GoogleButton onClick={google} loading={loading} testid="auth-modal-google-btn" />
          <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.2em] text-ink-muted">
            <div className="flex-1 h-px bg-ink-line" /> or <div className="flex-1 h-px bg-ink-line" />
          </div>
          {mode === "register" && (
            <div>
              <label className="stat-label block mb-1.5">Full name</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className={inputCls} data-testid="auth-name-input" />
            </div>
          )}
          <div>
            <label className="stat-label block mb-1.5">Email</label>
            <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className={inputCls} data-testid="auth-email-input" />
          </div>
          <div>
            <label className="stat-label block mb-1.5">Password {mode === "register" && <span className="text-ink-muted text-[10px]">(min 6 chars)</span>}</label>
            <input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={6} className={inputCls} data-testid="auth-password-input" />
          </div>

          {err && <div className="text-sm text-warn bg-warn-soft border border-warn/30 px-3 py-2 rounded-sm" data-testid="auth-modal-error">{err}</div>}

          <button
            type="submit"
            disabled={loading}
            data-testid="auth-modal-submit"
            className="w-full bg-brand hover:bg-brand-hover disabled:opacity-60 text-white font-medium py-2.5 rounded-sm text-sm transition-colors flex items-center justify-center gap-2"
          >
            {mode === "login" ? <LogIn className="w-4 h-4" /> : <UserPlus className="w-4 h-4" />}
            {loading ? "Please wait…" : (mode === "login" ? "Sign in & continue" : "Create account & continue")}
          </button>

          {mode === "login" && (
            <div className="text-center text-xs text-ink-muted">
              New to TWIN?{" "}
              <button type="button" onClick={() => setMode("register")} className="text-brand hover:underline">Create an account</button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
